import { useBlocker, UNSAFE_DataRouterContext } from "react-router-dom";
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

const GuardContext = createContext(null);
// Files are compared by metadata; no form values are persisted or logged.
export const formSnapshot = value => JSON.stringify(value, (_, item) =>
  typeof File !== "undefined" && item instanceof File
    ? { name: item.name, size: item.size, type: item.type, lastModified: item.lastModified }
    : item
);

export function UnsavedChangesProvider({ children }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const forms = useRef(new Map());
  const bypass = useRef(false);
  const [pending, setPending] = useState(null);
  const request = useCallback(action => {
    if (!action) return;
    if (bypass.current) { action(); return; }
    const entries = [...forms.current.values()];
    if (entries.some(entry => entry.saving)) return;
    if (entries.some(entry => entry.dirty)) setPending(() => action);
    else action();
  }, []);
  useEffect(() => {
    const beforeUnload = event => {
      if ([...forms.current.values()].some(entry => entry.dirty || entry.saving)) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, []);
  const context = useMemo(() => ({ forms, request }), [request]);
  return <GuardContext.Provider value={context}>
    {children}
    {dataRouter && <RouteGuard forms={forms} bypass={bypass} />}
    <ConfirmDialog open={Boolean(pending)} title="Discard unsaved changes?" description="Your changes have not been saved. Keep editing or discard them to continue." confirmText="Discard changes" cancelText="Keep editing" variant="warning" onCancel={() => setPending(null)} onConfirm={() => {
      if ([...forms.current.values()].some(entry => entry.saving)) throw new Error("Save in progress");
      const action = pending;
      // Bypass only this close/navigation chain. Other mounted forms remain protected.
      setPending(null);
      bypass.current = true;
      try { action?.(); } finally { bypass.current = false; }
    }} />
  </GuardContext.Provider>;
}

function RouteGuard({ forms, bypass }) {
  const proceeding = useRef(false);
  const blocker = useBlocker(useCallback(() => !bypass.current && [...forms.current.values()].some(entry => entry.dirty || entry.saving), [forms, bypass]));
  return <ConfirmDialog open={blocker.state === "blocked"} title="Discard unsaved changes?" description="Keep editing or discard your changes to leave this page." confirmText="Discard changes" cancelText="Keep editing" variant="warning" onCancel={() => { if (!proceeding.current && blocker.state === "blocked") blocker.reset(); proceeding.current = false; }} onConfirm={() => {
    if ([...forms.current.values()].some(entry => entry.saving)) throw new Error("Save in progress");
    proceeding.current = true;
    if (blocker.state === "blocked") blocker.proceed();
  }} />;
}

export function useGuardedAction() {
  const context = useContext(GuardContext);
  return context?.request || (action => action?.());
}

/** Call markSaved only after API success; resetBaseline after an explicit reset.
 * enabled excludes hidden dialogs, and formKey resets the baseline when editing another record.
 */
export function useFormGuard({ values, initialValues = values, saving = false, enabled = true, formKey = "default" }) {
  const context = useContext(GuardContext);
  const id = useId();
  const saveLock = useRef(false);
  const current = formSnapshot(values);
  const initial = formSnapshot(initialValues);
  const wasEnabled = useRef(enabled);
  const baseline = useRef({ key: formKey, value: initial });
  if ((!wasEnabled.current && enabled) || baseline.current.key !== formKey) baseline.current = { key: formKey, value: initial };
  wasEnabled.current = enabled;
  const [, rerender] = useState(0);
  const dirty = current !== baseline.current.value;
  useLayoutEffect(() => {
    if (enabled) context?.forms.current.set(id, { dirty, saving });
    else context?.forms.current.delete(id);
  }, [context, id, dirty, saving, enabled]);
  useEffect(() => () => { context?.forms.current.delete(id); }, [context, id]);
  const resetBaseline = useCallback((nextValues = values) => {
    baseline.current.value = formSnapshot(nextValues);
    const entry = context?.forms.current.get(id);
    if (entry) { entry.dirty = false; entry.saving = false; }
    rerender(value => value + 1);
  }, [context, id, values]);
  const beginSave = () => {
    if (saveLock.current || saving) return false;
    saveLock.current = true;
    const entry = context?.forms.current.get(id);
    if (entry) entry.saving = true;
    return true;
  };
  const endSave = () => {
    saveLock.current = false;
    const entry = context?.forms.current.get(id);
    if (entry) entry.saving = false;
  };
  return { beginSave, endSave, dirty, markSaved: resetBaseline, resetBaseline, request: context?.request || (action => action?.()) };
}
